import { Component, OnInit, Input, Output, EventEmitter, OnChanges } from '@angular/core';
import { Location } from '@angular/common';
import { UntypedFormBuilder, UntypedFormGroup, FormArray, FormControl, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { AlertService } from '@app/_services';
import { ApiResponse } from '@app/_models';
import { LazyLoadEvent, MessageService, ConfirmationService } from 'primeng/api';
import { RateBucketService } from '.';
import { take } from 'rxjs/operators';
import { RateBucket } from './rate-bucket';
import { MatDialog, MatDialogConfig, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { RateBucketFormComponent } from './rate-bucket-form/rate-bucket-form.component';
import { DialogService } from 'primeng/dynamicdialog';
import { RateBucketCopyFormComponent } from './rate-bucket-copy-form/rate-bucket-copy-form.component';

@Component({
    selector: 'app-rate-bucket',
    templateUrl: './rate-bucket.component.html',
    styleUrls: ['./rate-bucket.component.css'],
    providers: [DialogService, MessageService, ConfirmationService, { provide: MAT_DIALOG_DATA, useValue: {} }],
    // make column heading sticky
    styles: [`
        :host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
            position: -webkit-sticky;
            position: sticky;
            top: 0px;
        }

        @media screen and (max-width: 64em) {
            :host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
                top: 99px;
            }
        }
`],
    standalone: false
})
export class RateBucketComponent implements OnInit {

	constructor(
		private router: Router,
		private activatedRoute: ActivatedRoute,
		private rateBucketService: RateBucketService,
		private alertService: AlertService,
		private messageService: MessageService,
		private confirmationService: ConfirmationService,
		private dialog: MatDialog,
		private location: Location,
		private formBuilder: UntypedFormBuilder,
		public dialogService: DialogService) { }


	// structure
	subscription: Subscription;
	routerLocation = this.location;

	// data
	rateUserApiResponse: ApiResponse;
	rateBuckets: any;

	// paging
	first = 0;
	rows = 50;

	// list
	datasource;

	recordTotal: number;
	lazyLoadEvent: LazyLoadEvent;
	loading = true;
	multiSortMeta = [];

	// forms
	addForm: UntypedFormGroup;
	editForm: UntypedFormGroup;
	formLoading = false;
	formLabels = [];
	formReadOnly = false;

	columns: any[] = [
		{ field: 'rateBucketId', header: 'Id', width: 'auto' },
		{ field: 'name', header: 'Name', width: 'auto' },
		{ field: 'description', header: 'Description', width: 'auto' },
		{ field: 'lastUpdate', header: 'Created', width: 'auto' },
		{ field: 'createDate', header: 'Updated', width: 'auto' },
	];


	validationMessages = {
		'name': [
			{ type: 'required', message: 'Required' }
		],
		'description': [
			{ type: 'required', message: 'Required' }
		],

	}

	ngOnInit(): void {
		try {
			this.loadRateBuckets();

			// default sort of list
			this.multiSortMeta = [];
			this.multiSortMeta.push({ field: 'createDate', order: 0 });
		}
		catch (error) {
			console.log('Error: ' + error);
		}
	}

	/*********
	 * Cell Edit Callbacks (if using cell edit feature).
	 * Remove editMode="row" in p-table
	 * Remove <tr [pEditableRow]="rateBucket"> and just have plain <tr>
	 * Add <td [pEditableColumn]="rateBucket" [pEditableColumnField]="'name'"> for each cell <td>
	 * Update <ng-template pTemplate="body" let-rateBucket>
	 *
	 */
	onCellEditInit(event): void { }
	onCellEditComplete(event): void { this.formUpdate(event.data) }
	onCellEditCancel(): void { this.messageService.add({ severity: 'info', summary: 'Update Canceled', detail: 'No changes were saved' }); }



	/*********
	 * Row Edit Callbacks (if using row edit feature)
	 * Add editMode="row" in p-table
	 * Add <tr [pEditableRow]="rateBucket"> for the <tr>
	 * Remove <td [pEditableColumn]="rateBucket" [pEditableColumnField]="'name'"> for each cell <td>
	 * <ng-template pTemplate="body" let-rateBucket let-editing="editing" let-ri="rowIndex">
	 */
	onRowEditInit(rateBucket: RateBucket): void { }
	onRowEditSave(rateBucket: RateBucket): void { }
	onRowEditCancel(): void { this.messageService.add({ severity: 'info', summary: 'Update Canceled', detail: 'No changes were saved' }); }



	/*********
	 * Delete Button
	 *
	 */
	onRowDelete(event) {
		console.log('onRowDelete: ' + JSON.stringify(event));
		this.confirmationService.confirm({
			message: 'Are you sure you want to delete Id# ' + event.rateBucketId + '?',
			header: 'Confirm',
			icon: 'pi pi-exclamation-triangle',
			accept: () => {
				this.formDelete(event);
			},
			reject: () => {
				this.messageService.add({ severity: 'info', summary: 'Canceled', detail: 'Delete canceled.', life: 3000 });
			}
		});
	}


	/*********
	 * Load All data (not lazy load)
	 *
	 */
	loadRateBuckets() {
		try {
			this.formLoading = true;

			// use the .pipe(take(1)) to prevent double firing...
			this.rateBucketService.findAll().pipe(take(1))
				.subscribe(
					res => {
						// console.log('Rate-Bucket-Load SUCCESS: ' + JSON.stringify(res));
						this.alertService.success(res, true);
						this.rateBuckets = res.data.rateBuckets;
					},
					err => {
						console.log('Rate-Bucket-Load FAIL: ' + JSON.stringify(err));
						this.messageService.add({ severity: 'error', summary: JSON.stringify(err), life: 3000 });
					},
					() => { this.formLoading = false; });

		} catch (error) {
			this.alertService.error(error);
		}
	}

	// handled in rate-bucket-form
	formAdd() { }


	// used for row editing
	formUpdate(rateBucket: RateBucket) {
		try {
			this.formLoading = true;
			this.rateBucketService.update(rateBucket)
				.subscribe(
					res => {
						console.log('Rate-Bucket-Update SUCCESS: ' + JSON.stringify(res));
						this.rateBuckets.map((rateBucket, i) => {
							if (rateBucket.rateBucketId === res.data.rateBucket.rateBucketId) {
								this.rateBuckets[i] = res.data.rateBucket;
							}
						});
						// this.alertService.success(res, true);
						this.messageService.add({ severity: 'success', summary: 'Success', detail: res.message });
					},
					err => {
						console.log('Rate-Bucket-Update FAIL: ' + JSON.stringify(err));
						this.messageService.add({ severity: 'error', summary: JSON.stringify(err), life: 3000 });
					},
					() => { this.formLoading = false; });
		} catch (error) {
			console.log('Form Invalid: ' + JSON.stringify(error));
			this.alertService.error(error);
		}
	}

	// used for row editing
	formDelete(rateBucket) {
		try {
			// stop here if form is invalid
			console.log('Delete Submitted: ' + JSON.stringify(rateBucket));

			this.formLoading = true;
			this.rateBucketService.delete(rateBucket.rateBucketId)
				.subscribe(
					res => {
						console.log('Rate-Bucket-Delete SUCCESS: ' + JSON.stringify(res));
						this.rateBuckets.splice(this.rateBuckets.indexOf(rateBucket), 1);
						this.recordTotal = this.rateBuckets.length;
						this.messageService.add({ severity: 'success', summary: 'Successful', detail: 'RateBucket ' + rateBucket.rateBucketId + ' Deleted', life: 3000 });
					},
					err => {
						console.log('Rate-Bucket-Delete  FAIL: ' + JSON.stringify(err));
						this.messageService.add({ severity: 'error', summary: 'Delete Failed', detail: 'RateBucket ' + rateBucket.rateBucketId + ' could not be deleted.  Be sure there are no assigned properties to this bucket.', life: 3000 });
					},
					() => { this.formLoading = false; });
		} catch (error) {
			console.log('Form Invalid: ' + JSON.stringify(error));
			this.alertService.error(error);
		}
	}

	/*********
		 * Add New Button
		 *
		 */
	onOpenFormAdd() {

		const dialogConfig = new MatDialogConfig();

		// settings
		dialogConfig.disableClose = false;
		dialogConfig.autoFocus = true;
		dialogConfig.hasBackdrop = true;

		// pass data, ie rateBuicket: this.shortTermRentalId,
		dialogConfig.data = {
			title: 'Add RateBucket'
		};

		// open the popup and get a reference to it called dialogRef
		const dialogRef = this.dialog.open(RateBucketFormComponent, dialogConfig);

		// to receive the added element, subscribe to the eventEmitter we created 'doAdd' in the element to handle adding
		dialogRef.componentInstance.doAdd.subscribe(
			data => {
				console.log('Dialog output:', data);

				// when we doAdd in the element, call this parent and update the list so it shows the added element to the database
				this.addRateBucketCB(data);

				// close the dialogBox
				dialogRef.close();
			});
	}


	/*********
		* Edit Button
		*
		*/
	onOpenFormEdit(rateBucket: RateBucket) {

		const dialogConfig = new MatDialogConfig();

		// settings
		dialogConfig.disableClose = false;
		dialogConfig.autoFocus = true;
		dialogConfig.hasBackdrop = true;

		// pass data, ie rateBuicket: this.shortTermRentalId,
		dialogConfig.data = {
			rateBucket: rateBucket,
			title: 'Edit RateBucket'
		};

		// open the popup and get a reference to it called dialogRef
		const dialogRef = this.dialog.open(RateBucketFormComponent, dialogConfig);

		// to receive the added element, subscribe to the eventEmitter we created 'doUpdate' in the element to handle updating the list
		dialogRef.componentInstance.doUpdate.subscribe(
			data => {
				console.log('Dialog output:', data);

				// when we doAdd in the element, call this parent and update the list so it shows the added element to the database
				this.updateRateBucketCB(data);

				// close the dialogBox
				dialogRef.close();
			});

		dialogRef.componentInstance.doDelete.subscribe(
			data => {
				console.log('Dialog output:', data);

				// when we doAdd in the element, call this parent and update the list so it shows the added element to the database
				this.deleteRateBucketCB(data);

				// close the dialogBox
				dialogRef.close();
			});
	}

	/*********
		  * Copy Button
		  * made with primeng
		  */
	onOpenFormCopy(rateBucket: RateBucket) {
		const ref = this.dialogService.open(RateBucketCopyFormComponent, {
			data: {
				rateBucket: rateBucket
			},
			header: 'Copy ' + rateBucket.name + '  (#' + rateBucket.rateBucketId + ')',
			width: 'auto'
		});

		ref.onClose.subscribe((form1: any) => {
			if (form1) {
				this.copyRateBucket(form1);


			}
		});

	}

	copyRateBucket(rateBucket: RateBucket) {
		try {
			this.formLoading = true;
			this.rateBucketService.copy(rateBucket)
				.subscribe(
					res => {
						console.log('Rate-Bucket-Copy SUCCESS: ' + JSON.stringify(res));

						// this.alertService.success(res, true);
						this.messageService.add({ severity: 'success', summary: 'Success', detail: 'New Rate Bucket ' + rateBucket.name + ' created.' });
					},
					err => {
						console.log('Rate-Bucket-Update FAIL: ' + JSON.stringify(err));
						this.messageService.add({ severity: 'error', summary: JSON.stringify(err), life: 3000 });
					},
					() => { this.formLoading = false; });
		} catch (error) {
			console.log('Form Invalid: ' + JSON.stringify(error));
			this.alertService.error(error);
		}
	}




	/**
	   *
	   * Dialog from Form Callbacks
	   * To edit the list show add/edit/delete in real time from the popup form
	   *
	   */

	addRateBucketCB(rateBucket: RateBucket) {
		console.log('Adding RateBucket: ' + JSON.stringify(rateBucket));
		// if we don't have a rateBuckets array yet...
		if (!this.rateBuckets) {
			this.rateBuckets = [];
		}
		// unshift adds to the front of the array (), push adds to the back
		this.rateBuckets.unshift(rateBucket);

		// no idea what this is but it is required to update the array
		this.rateBuckets = [...this.rateBuckets];

		this.messageService.add({ severity: 'success', summary: 'Success', detail: 'RateBucket ' + rateBucket.rateBucketId + ' Added', life: 3000 });
	}


	updateRateBucketCB(rateBucket: RateBucket) {
		console.log('Updating RateBucket: ' + JSON.stringify(rateBucket));
		let itemIndex = this.rateBuckets.findIndex(item => item.rateBucketId === rateBucket.rateBucketId);
		this.rateBuckets[itemIndex] = rateBucket;

		// no idea what this is but it is required to update the array
		this.rateBuckets = [...this.rateBuckets];

		this.messageService.add({ severity: 'success', summary: 'Success', detail: 'RateBucket ' + rateBucket.rateBucketId + ' Updated', life: 3000 });
	}

	deleteRateBucketCB(rateBucket: RateBucket) {
		console.log('Removing RateBucket: ' + rateBucket);
		this.rateBuckets.splice(this.rateBuckets.indexOf(rateBucket), 1);
		this.recordTotal = this.rateBuckets.length;

		// no idea what this is but it is required to update the array
		this.rateBuckets = [...this.rateBuckets];

		this.messageService.add({ severity: 'success', summary: 'Success', detail: 'RateBucket ' + rateBucket.rateBucketId + ' Deleted', life: 3000 });
	}

	/**********
	 *
	 * PAGINATOR FUNCTIONS
	 */

	next() { this.first = this.first + this.rows; }
	prev() { this.first = this.first - this.rows; }
	reset() { this.first = 0; }
	isLastPage(): boolean { return this.rateBuckets ? this.first === (this.rateBuckets.length - this.rows) : true; }
	isFirstPage(): boolean { return this.rateBuckets ? this.first === 0 : true; }


}
